import React, { useState, useMemo, useCallback } from 'react';
import styles from './AdminTable.module.css';
import tableStyles from './AdminDashboard.module.css';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface ColumnDef<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render: (item: T) => React.ReactNode;
  exportValue?: (item: T) => string;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDef {
  key: string;
  label: string;
  options: FilterOption[];
}

interface AdminTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  filters?: FilterDef[];
  filterFn?: (item: T, activeFilters: Record<string, string>) => boolean;
  searchFn?: (item: T, query: string) => boolean;
  onRowClick?: (item: T) => void;
  getId: (item: T) => string;
  onBulkDelete?: (ids: string[]) => void;
  exportFilename?: string;
  loading?: boolean;
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  headerTitle: string;
  headerAction?: React.ReactNode;
  pageSize?: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export function AdminTable<T>({
  data,
  columns,
  filters = [],
  filterFn,
  searchFn,
  onRowClick,
  getId,
  onBulkDelete,
  exportFilename = 'export',
  loading,
  emptyIcon,
  emptyTitle = 'Geen data',
  emptyDescription = 'Er zijn nog geen items.',
  emptyAction,
  headerTitle,
  headerAction,
  pageSize = 15,
}: AdminTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ── Filter + Search ────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let result = data;

    // Search
    if (searchQuery.trim() && searchFn) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((item) => searchFn(item, q));
    }

    // Filters
    const hasActiveFilters = Object.values(activeFilters).some((v) => v !== '');
    if (hasActiveFilters && filterFn) {
      result = result.filter((item) => filterFn(item, activeFilters));
    }

    // Sort
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col?.exportValue) {
        result = [...result].sort((a, b) => {
          const valA = col.exportValue!(a).toLowerCase();
          const valB = col.exportValue!(b).toLowerCase();
          const cmp = valA.localeCompare(valB, 'nl');
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [data, searchQuery, searchFn, activeFilters, filterFn, sortKey, sortDir, columns]);

  // ── Pagination ─────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedData = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, safeCurrentPage, pageSize]);

  // Reset page on filter/search change
  const prevFilteredLen = React.useRef(filteredData.length);
  React.useEffect(() => {
    if (filteredData.length !== prevFilteredLen.current) {
      setCurrentPage(1);
      prevFilteredLen.current = filteredData.length;
    }
  }, [filteredData.length]);

  // ── Selection ──────────────────────────────────────────────────────────
  const allOnPageSelected = paginatedData.length > 0 && paginatedData.every((item) => selectedIds.has(getId(item)));

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        paginatedData.forEach((item) => next.delete(getId(item)));
      } else {
        paginatedData.forEach((item) => next.add(getId(item)));
      }
      return next;
    });
  }, [allOnPageSelected, paginatedData, getId]);

  const toggleSelect = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    []
  );

  // ── Sort handler ───────────────────────────────────────────────────────
  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey]
  );

  // ── Export ─────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    const exportCols = columns.filter((c) => c.exportValue);
    if (exportCols.length === 0) return;

    const header = exportCols.map((c) => `"${c.label}"`).join(';');
    const rows = filteredData.map((item) =>
      exportCols.map((c) => `"${(c.exportValue!(item) || '').replace(/"/g, '""')}"`).join(';')
    );

    const bom = '\uFEFF';
    const csv = bom + [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFilename}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [columns, filteredData, exportFilename]);

  // ── Bulk delete ────────────────────────────────────────────────────────
  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0 || !onBulkDelete) return;
    onBulkDelete(Array.from(selectedIds));
    setSelectedIds(new Set());
  }, [selectedIds, onBulkDelete]);

  // ── Filter change handler ──────────────────────────────────────────────
  const handleFilterChange = useCallback((key: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return <div className={tableStyles.loading}>Laden...</div>;
  }

  if (data.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableHeaderBar}>
          <h2>{headerTitle}</h2>
          {headerAction}
        </div>
        <div className={tableStyles.emptyState}>
          {emptyIcon && <div className={tableStyles.emptyIcon}>{emptyIcon}</div>}
          <h3>{emptyTitle}</h3>
          <p>{emptyDescription}</p>
          {emptyAction}
        </div>
      </div>
    );
  }

  const hasExportCols = columns.some((c) => c.exportValue);

  return (
    <div className={styles.tableContainer}>
      {/* Header */}
      <div className={styles.tableHeaderBar}>
        <h2>{headerTitle}</h2>
        <div className={styles.headerActions}>
          {hasExportCols && (
            <button className={styles.exportBtn} onClick={handleExport} title="Exporteer als CSV">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export
            </button>
          )}
          {headerAction}
        </div>
      </div>

      {/* Toolbar: search + filters */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Zoeken..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {filters.map((filter) => (
          <select
            key={filter.key}
            value={activeFilters[filter.key] || ''}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">{filter.label}</option>
            {filter.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ))}

        {(searchQuery || Object.values(activeFilters).some((v) => v !== '')) && (
          <button
            className={styles.clearFilters}
            onClick={() => {
              setSearchQuery('');
              setActiveFilters({});
            }}
          >
            Wis filters
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedIds.size} geselecteerd</span>
          {onBulkDelete && (
            <button className={styles.bulkDeleteBtn} onClick={handleBulkDelete}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Verwijder selectie
            </button>
          )}
          <button
            className={styles.bulkClearBtn}
            onClick={() => setSelectedIds(new Set())}
          >
            Deselecteer
          </button>
        </div>
      )}

      {/* Results count */}
      <div className={styles.resultsInfo}>
        {filteredData.length} van {data.length} resultaten
        {filteredData.length !== data.length && (
          <span className={styles.filteredNote}> (gefilterd)</span>
        )}
      </div>

      {/* Table */}
      <div className={tableStyles.tableWrapper}>
        <table className={tableStyles.dataTable}>
          <thead>
            <tr>
              {onBulkDelete && (
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAll}
                    className={styles.checkbox}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  className={col.sortable ? styles.sortableHeader : undefined}
                >
                  <span className={styles.thContent}>
                    {col.label}
                    {col.sortable && sortKey === col.key && (
                      <span className={styles.sortArrow}>
                        {sortDir === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (onBulkDelete ? 1 : 0)}
                  className={styles.noResults}
                >
                  Geen resultaten voor "{searchQuery}"
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => {
                const id = getId(item);
                return (
                  <tr
                    key={id}
                    onClick={() => onRowClick?.(item)}
                    style={{ cursor: onRowClick ? 'pointer' : undefined }}
                    className={selectedIds.has(id) ? styles.selectedRow : undefined}
                  >
                    {onBulkDelete && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(id)}
                          onChange={() => toggleSelect(id)}
                          className={styles.checkbox}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key}>{col.render(item)}</td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            onClick={() => setCurrentPage(1)}
            disabled={safeCurrentPage === 1}
          >
            «
          </button>
          <button
            className={styles.pageBtn}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={safeCurrentPage === 1}
          >
            ‹
          </button>

          {generatePageNumbers(safeCurrentPage, totalPages).map((p, i) =>
            p === '...' ? (
              <span key={`dots-${i}`} className={styles.pageDots}>
                …
              </span>
            ) : (
              <button
                key={p}
                className={`${styles.pageBtn} ${safeCurrentPage === p ? styles.pageActive : ''}`}
                onClick={() => setCurrentPage(p as number)}
              >
                {p}
              </button>
            )
          )}

          <button
            className={styles.pageBtn}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={safeCurrentPage === totalPages}
          >
            ›
          </button>
          <button
            className={styles.pageBtn}
            onClick={() => setCurrentPage(totalPages)}
            disabled={safeCurrentPage === totalPages}
          >
            »
          </button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function generatePageNumbers(current: number, total: number): (number | string)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | string)[] = [1];
  if (current > 3) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('...');
  pages.push(total);

  return pages;
}
